import React, { useMemo } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  FormattedMessage,
  FormattedNumber,
} from '~/techstyle-shared/react-intl';

import logger from '../../utils/logger';
import { Callout } from '../../utils/useProductCallout';
import PreorderTag from '../PreorderTag';

const showYittyCollectionCallout = config.get(
  'public.brand.features.showYittyCollectionCallout'
);

const debug = logger.extend('ProductCallout');

const Wrapper = styled.div``;

function ProductCallout({ value, type, ...rest }) {
  const calloutMessage = useMemo(() => {
    switch (type) {
      case Callout.YITTY:
        return <FormattedMessage id="app_grid.yitty" defaultMessage="YITTY" />;
      case Callout.YITTY_COLLECTION:
        return showYittyCollectionCallout ? (
          <FormattedMessage
            id="app_grid.yitty_collection"
            defaultMessage="YITTY COLLECTION"
          />
        ) : null;
      case Callout.DELAYED_SHIPPING:
        return (
          <FormattedMessage
            id="app_grid.delayed_shipping"
            defaultMessage="DELAYED SHIPPING"
          />
        );
      case Callout.PREORDER:
        return <PreorderTag />;
      case Callout.EU_EXCLUSIVE:
        return (
          <FormattedMessage
            id="system_product_detail.e24b4a9875311f765ac713146e90b04e"
            defaultMessage="EU EXCLUSIVE"
          />
        );
      case Callout.SOLD_OUT:
        return (
          <FormattedMessage
            id="app_grid.callout_sold_out"
            defaultMessage="SOLD OUT"
          />
        );
      case Callout.FAKE_SOLD_OUT:
        return (
          <FormattedMessage
            id="app_grid.callout_fake_sold_out"
            defaultMessage="SOLD OUT"
          />
        );
      case Callout.MENS_FAKE_SOLD_OUT:
        return (
          <FormattedMessage
            id="app_grid.callout_fake_sold_out_male"
            defaultMessage="SOLD OUT"
          />
        );
      case Callout.PLATINUM_EXCLUSIVE:
        return (
          <FormattedMessage
            id="grid.callout_platinum_exclusive"
            defaultMessage="EXCLUSIVE + PLATINUM"
          />
        );
      case Callout.KATES_EDIT:
        return (
          <FormattedMessage
            id="grid.callout_kates_edit"
            defaultMessage="KATE'S EDIT"
          />
        );
      case Callout.ECO_STYLE:
        return (
          <FormattedMessage
            id="grid.callout_eco_style"
            defaultMessage="ECO STYLE"
          />
        );
      case Callout.BACK_IN_STOCK:
        return (
          <FormattedMessage
            id="grid.callout_back_in_stock"
            defaultMessage="BACK IN STOCK"
          />
        );
      case Callout.WAITLIST_AVAILABLE:
        return (
          <FormattedMessage
            id="grid.callout_waitlist_available"
            defaultMessage="WAITLIST AVAILABLE"
          />
        );
      case Callout.CRITICAL_STOCK:
      case Callout.LOW_STOCK:
        return (
          <FormattedMessage
            id="grid.callout_low_stock"
            defaultMessage="LOW STOCK"
          />
        );
      case Callout.ONLY_X_LEFT:
        return (
          <FormattedMessage
            id="grid.callout_only_x_left"
            defaultMessage="ONLY {1} LEFT"
            values={{ 1: <FormattedNumber value={value} /> }}
          />
        );
      case Callout.GOLD_ELITE_EXCLUSIVE:
        // NEW RB GROUP. MOVED FROM RB GROUP: SITE_PDP
        return (
          <FormattedMessage
            id="site_product_grid.vip_gold_elite_title"
            defaultMessage="GOLD/ELITE EXCLUSIVE"
          />
        );
      case Callout.TOP_RATED:
        return (
          <FormattedMessage
            id="grid.top_rated_label"
            defaultMessage="Top Rated"
          />
        );
      case Callout.NEW:
        return <FormattedMessage id="grid.callout_new" defaultMessage="NEW" />;
      case Callout.NEW_TAG:
        return (
          <FormattedMessage id="grid.callout_new_tag" defaultMessage="NEW" />
        );
      case Callout.SOLD_OUT_KEEP_ON_GRID:
        return (
          <FormattedMessage
            id="grid.callout_sold_out_keep_on_grid"
            defaultMessage="SELLING FAST, MORE COMING SOON!"
          />
        );
      case Callout.LAST_CHANCE:
        return (
          <FormattedMessage
            id="grid.callout_last_chance"
            defaultMessage="LAST CHANCE"
          />
        );
      case Callout.KHLOES_PICK:
        return (
          <FormattedMessage
            id="grid.callout_khloes_pick"
            defaultMessage="KHLOE&rsquo;S PICK"
          />
        );

      case Callout.CUSTOM_TAG:
        return (
          <FormattedMessage
            id="grid.custom_tag_label"
            defaultMessage="Custom Tag"
          />
        );
      case Callout.RIHANNAS_PICK:
        return (
          <FormattedMessage
            id="grid.rihannas_pick_label"
            defaultMessage="Rihanna’s Pick"
          />
        );
      case Callout.COMING_SOON:
        return (
          <FormattedMessage
            id="grid.coming_soon_label"
            defaultMessage="Coming Soon"
          />
        );
      case Callout.BEST_SELLER:
        return (
          <FormattedMessage
            id="grid.best_seller_label"
            defaultMessage="Best seller"
          />
        );
      case Callout.TRENDING:
        return (
          <FormattedMessage
            id="grid.trending_label"
            defaultMessage="Trending"
          />
        );
      case Callout.YOUNIVERSAL:
        return (
          <FormattedMessage
            id="grid.youniversal"
            defaultMessage="YOUniversal"
          />
        );
      case Callout.FINAL_SALE:
        return (
          <FormattedMessage id="grid.final_sale" defaultMessage="FINAL SALE" />
        );
      default:
        // debug('No callouts matched %s', type);
        return null;
    }
  }, [type, value]);

  debug('calloutMessage', calloutMessage);

  return (
    calloutMessage && (
      <Wrapper data-autotag="grid-product-callout" {...rest}>
        {calloutMessage}
      </Wrapper>
    )
  );
}

ProductCallout.propTypes = {
  type: PropTypes.string,
  value: PropTypes.any,
};

export default ProductCallout;
